import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// actions
import {
  setCustomizeStepData,
  setStepKey,
  updateCustomizationsData,
} from 'cards/reducers/singleSampleFlow/actions';

// selectors
import {
  getCardSuiteDetailsSet,
  getCardType,
  getCustomizationsSet,
  getCustomizeData,
} from 'cards/selectors/singleSampleFlow';
import { getPricing } from 'cards/reducers/pricing/selectors';

// components
import OptionSection from 'cards/components/CardDetail/OptionSection/OptionSection';
import { Link } from '@zola/zola-ui/src/components/Link';

// helpers
import { useIsMobile } from 'paper/hooks/isMobile';
import {
  enrichCardLevelOptions,
  enrichOptionsAccSelection,
  getCurrentOptionDimension,
  getCurrentOptionLabel,
  getEnabledOptionsValuesList,
  isComparisonModuleHidden,
} from 'cards/components/ProductDetail/AllOptionsSection/utils';
import { SETTINGS_BY_CARD_TYPE } from 'cards/constants/CardTypes';
import Form from '../../Form';
import { STEPS } from '../../constants';
import { lookupSelectedVariation } from '../../../util/helpers';

import styles from './styles.module.less';

const optionKeys: string[] = ['size', 'foil-color', 'paper-type', 'silhouette'];

const styleOverrides = {
  optionSectionTitleArea: styles.optionSectionTitleArea,
  optionSectionTitle: styles.optionSectionTitle,
  optionSectionToggleContainer: styles.optionSectionToggleContainer,
  toggleList: styles.toggleList,
  item: styles.item,
  pdpToggleButton: styles.pdpToggleButton,
};

interface CustomizeStepProps {
  cardSuiteUUIDList: string[];
  isAdminView: boolean;
  isMagnet: boolean;
  showPaperTypeModal: (paperTypeModalProps: any) => void;
}

const CustomizeStep = ({
  cardSuiteUUIDList,
  isAdminView,
  isMagnet,
  showPaperTypeModal,
}: CustomizeStepProps): JSX.Element | null => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const cardSuiteDetailsSet = useSelector(getCardSuiteDetailsSet);
  const cardType = useSelector(getCardType);
  const customizeData = useSelector(getCustomizeData);
  const customizationsSet = useSelector(getCustomizationsSet);
  const pricing = useSelector(getPricing);
  const { options: customizeOptions, activeSelections } = customizeData;

  if (!customizeOptions || !cardType) return null;

  const cardUrlSlug = SETTINGS_BY_CARD_TYPE[cardType].urlSlug;

  const nextBtnText = 'Next: Shipping Address';
  const nextStep = () => {
    Object.values(cardSuiteDetailsSet).forEach((cardSuiteDetails: any) => {
      const cardSuiteUUID = cardSuiteDetails.uuid;
      const { variation } = customizationsSet[cardSuiteUUID];

      const selectedOptions = {
        ...variation.option_values,
        ...activeSelections,
        foil: activeSelections['foil-color'] !== 'none' ? 'custom' : 'none',
      };
      const newVariation = lookupSelectedVariation(selectedOptions, cardSuiteDetails, isMagnet);

      dispatch(updateCustomizationsData(cardSuiteUUID, { variation: newVariation }));
    });

    dispatch(setStepKey(STEPS.SHIPPING));
  };
  const prevBtnText = 'Back';
  const prevStep = () => dispatch(setStepKey(STEPS.PHOTO));

  return (
    <>
      <Form.Header
        currentStep={3}
        description={
          <>
            Make it feel like you in just a few clicks. Want to sample <b>magnets</b> or customize
            more? See all our options{' '}
            <Link
              href={`/wedding-planning/${cardUrlSlug}/${cardSuiteUUIDList[0]}`}
              target="_blank"
              inline
            >
              here
            </Link>
            , and order a proof at checkout instead.
          </>
        }
        steps={4}
        title="Customize Your Paper"
      />
      <div className={styles.wrapper}>
        {optionKeys.map(optionKey => {
          const uiData = customizeOptions[optionKey];

          if (!uiData) return null;

          const { key, options, label } = uiData;
          const selection = options.find(
            (option: any) => option.value === activeSelections[optionKey]
          );
          const enabledOptionsValuesList = getEnabledOptionsValuesList(
            options,
            cardType,
            key,
            isAdminView
          );
          const cardTypeOptions = enrichCardLevelOptions(
            key,
            options,
            cardType,
            enabledOptionsValuesList,
            isMagnet ? 'magnet' : 'paper',
            isAdminView
          );
          const enrichedOptions = enrichOptionsAccSelection(
            key,
            cardTypeOptions,
            activeSelections,
            cardType,
            pricing,
            isMagnet ? 'magnet' : 'paper'
          );

          const optionType = key;
          const currentSelection = {
            type: optionType,
            ...selection,
          };
          const currentLabel = getCurrentOptionLabel({ selection, optionType, cardType });

          return (
            <OptionSection
              key={optionKey}
              cardType={cardType}
              title={label}
              options={enrichedOptions}
              selection={currentSelection}
              setOptionValue={option => dispatch(setCustomizeStepData(option))}
              styleOverrides={styleOverrides}
              currentLabel={currentLabel}
              currentDimension={getCurrentOptionDimension(selection, cardType)}
              optionType={optionType}
              isMagnet={isMagnet}
              isLinkHidden={isComparisonModuleHidden(cardType, enabledOptionsValuesList)}
              showLinkOnMobile={isMobile}
              titleLink="Compare Our Paper Types"
              titleLinkAction={showPaperTypeModal}
              hoverDisabled
            />
          );
        })}
        <Form.ButtonWrapper>
          <Form.Button
            btnText={isMobile ? nextBtnText : prevBtnText}
            handleClick={isMobile ? nextStep : prevStep}
            variant={isMobile ? 'primary' : 'secondary'}
          />
          <Form.Button
            btnText={isMobile ? prevBtnText : nextBtnText}
            handleClick={isMobile ? prevStep : nextStep}
            variant={isMobile ? 'secondary' : 'primary'}
          />
        </Form.ButtonWrapper>
      </div>
    </>
  );
};

export default CustomizeStep;
